/*
 * This illustrates how to create a widget that makes use of a number of different field definition types.
 */

import { xHeaderActions, xHeaderState } from "@eisinc/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { screen } from "@eisinc/XDL/Helpers";
import Close from "@eisinc/XDL/Icons/Close";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { ColorPickerFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

export interface GlobalWebsiteMessageStyles {
    container: InjectableCss;
    close: ButtonPresentationProps;
}

export const globalWebsiteMessageStyles: GlobalWebsiteMessageStyles = {
    container: {
        css: css`
            min-height: 50px;
            text-align: center;
            font-size: 15px;
            display: flex;
            align-items: center;
            justify-content: center;

            p {
                margin: 0px 1px;
                line-height: 1;
            }

            ${screen(
            "lte",
            "sm",
        )`* {
                    font-size: 12px;
                    color: #000;
                    text-align: left;
                }
                padding: 0 10px;
                padding-right: 30px;`
            }
            ${({ backgroundColor }: any) => backgroundColor && `background: ${backgroundColor};`}
            ${({ globalMessageState }: any) => !globalMessageState && `display: none;`}
        `,
    },
    close: {
        css: css`
            position: absolute;
            right: 15px;
            top: 15px;
        `,
    },
};

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    globalMessage = "globalMessage",
    backgroundColor = "backgroundColor",
}

const mapStateToProps = (state: xHeaderState) => ({
    isSticky: state.xHeader.isSticky,
    globalMessageState: state.xHeader.globalMessage,
});

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    DispatchProp<xHeaderActions> & {
        fields: {
            [fields.globalMessage]: string;
            [fields.backgroundColor]: ColorPickerFieldDefinition;
        };
    };

const GlobalWebsiteMessage: React.FC<Props> = ({
    fields: { backgroundColor, globalMessage },
    dispatch,
    isSticky,
    globalMessageState,
}) => {
    const onClose = () => {
        dispatch({ type: "xHeader/globalMessage", globalMessage: false });
    };
    return (
        <StyledWrapper
            {...globalWebsiteMessageStyles.container}
            {...({ backgroundColor, isSticky, globalMessageState } as any)}
        >
            <Clickable
                {...globalWebsiteMessageStyles.close}
                onClick={onClose}
                data-test-selector="global_sitemessage_close"
            >
                <Icon size={18} src={Close} />
            </Clickable>
            {globalMessage && parse(globalMessage, parserOptions)}
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Header",
    displayName: "Global Website Message",
    allowedContexts: ["Header"],
    fieldDefinitions: [
        {
            name: fields.globalMessage,
            displayName: "Global Message Content",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "your content goes here",
            isRequired: true,
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "",
            fieldType: "General",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(GlobalWebsiteMessage),
    definition,
};

export default widgetModule;
